<template>
  <span class="info-tooltip-wrapper" ref="wrapperRef">
    <button
      type="button"
      class="info-tooltip-trigger"
      :class="{ active: isOpen }"
      @click.stop="toggle"
      @mouseenter="onHover(true)"
      @mouseleave="onHover(false)"
      :aria-label="ariaLabel || 'More information'"
      :aria-expanded="isOpen"
    >
      <font-awesome-icon :icon="['fas', 'circle-info']" />
    </button>
    <transition name="info-fade">
      <div
        v-if="isOpen"
        class="info-tooltip-bubble"
        :class="`pos-${position}`"
        role="tooltip"
      >
        <slot>{{ text }}</slot>
      </div>
    </transition>
  </span>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue';

export default {
  name: 'InfoTooltip',
  props: {
    text: { type: String, default: '' },
    ariaLabel: { type: String, default: '' },
    position: {
      type: String,
      default: 'top', // 'top' | 'bottom' | 'left' | 'right'
      validator: (v) => ['top', 'bottom', 'left', 'right'].includes(v),
    },
  },
  setup() {
    const isOpen = ref(false);
    const wrapperRef = ref(null);
    let hoverTimeout = null;

    const toggle = () => {
      isOpen.value = !isOpen.value;
    };

    const onHover = (entering) => {
      // Only honor hover on devices that support it (skip on touch)
      if (!window.matchMedia('(hover: hover)').matches) return;
      clearTimeout(hoverTimeout);
      if (entering) {
        isOpen.value = true;
      } else {
        hoverTimeout = setTimeout(() => {
          isOpen.value = false;
        }, 150);
      }
    };

    const handleOutsideClick = (e) => {
      if (wrapperRef.value && !wrapperRef.value.contains(e.target)) {
        isOpen.value = false;
      }
    };

    onMounted(() => {
      document.addEventListener('click', handleOutsideClick);
    });
    onBeforeUnmount(() => {
      document.removeEventListener('click', handleOutsideClick);
      clearTimeout(hoverTimeout);
    });

    return { isOpen, wrapperRef, toggle, onHover };
  },
};
</script>

<style scoped>
.info-tooltip-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 1em;
}

.info-tooltip-trigger {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--gradient-primary);
  border: none;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(127, 119, 221, 0.25);
  transition:
    transform var(--transition-fast),
    box-shadow var(--transition-fast);
  flex-shrink: 0;
  position: relative;
  top: 1px; /* nudges down to optically center with text */
}
.info-tooltip-trigger:hover,
.info-tooltip-trigger.active {
  color: var(--color-accent-purple);
}
.dark-theme .info-tooltip-trigger {
  color: #6a7a8a;
}
.dark-theme .info-tooltip-trigger:hover,
.dark-theme .info-tooltip-trigger.active {
  color: var(--color-accent-teal);
}

.info-tooltip-bubble {
  position: absolute;
  z-index: 20;
  max-width: 240px;
  min-width: 180px;
  padding: 0.65rem 0.8rem;
  background: #213547;
  color: white;
  border-radius: var(--radius-sm);
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.45;
  box-shadow: var(--shadow-md);
  text-align: left;
  white-space: normal;
}
.dark-theme .info-tooltip-bubble {
  background: #2a3a4e;
  border: 1px solid #3a4a5e;
}

.info-tooltip-bubble.pos-top {
  bottom: calc(100% + 6px);
  transform: translateX(0%);
}
.info-tooltip-bubble.pos-bottom {
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
}
.info-tooltip-bubble.pos-left {
  right: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
}
.info-tooltip-bubble.pos-right {
  left: calc(100% + 6px);
  top: 50%;
  transform: translateY(-50%);
}

.info-fade-enter-active,
.info-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}
.info-fade-enter-from,
.info-fade-leave-to {
  opacity: 0;
}
.info-fade-enter-from.pos-top,
.info-fade-leave-to.pos-top {
  transform: translateY(4px);
}
.info-fade-enter-from.pos-bottom,
.info-fade-leave-to.pos-bottom {
  transform: translateY(-4px);
}
</style>
